import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { displayDollar, displayStatus } from '../../../lib/utils/helpers'
import {
  Withdrawal,
  WithdrawalApprovalDetails,
  WithdrawalStatus,
} from '../../../lib/types/propsType_v1'
import { withdrawalServices } from '../../../services'
import { withUpperScaleSuffix } from '../dashboard-v2.utils'
import { DashboardV2ProgressStepper } from './DashboardV2ProgressStepper'

type OngoingWithdrawalDetailsModalProps = {
  isOpen: boolean
  withdrawal: Withdrawal | null
  authToken: string
  clientId?: string
  onClose: () => void
}

type WithdrawalRequestMetadata = Withdrawal['metadata'] & {
  clientName?: string
  clientId?: string
  totalInvestedAmount?: number
  withdrawalAmount?: number
  redeemMode?: 'amount' | 'units'
  withdrawalUnits?: number
  underlyingAsset?: string
  withdrawalNoticePeriodDays?: number | null
  exitDealingChargesUsd?: number
  platformWithdrawalFeeUsd?: number
}

type DetailItemProps = {
  label: string
  value: string
}

const DetailItem: React.FC<DetailItemProps> = ({ label, value }) => (
  <div className='dashboard-v2__withdrawal-form__detail-item'>
    <span className='dashboard-v2__withdrawal-form__detail-label'>{label}</span>
    <span className='dashboard-v2__withdrawal-form__detail-value'>{value}</span>
  </div>
)

const formatUsdFromCents = (cents: number) =>
  withUpperScaleSuffix(displayDollar(String(Math.round(cents)), 'USD', 2, false))

const formatUsdFromDollars = (dollars?: number) => {
  if (dollars === undefined || !Number.isFinite(dollars)) return '—'
  return formatUsdFromCents(Math.round(dollars * 100))
}

const formatDate = (iso?: string) => {
  if (!iso) return '—'
  const d = new Date(iso.includes('T') ? iso : `${iso}T12:00:00`)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const formatPercent = (value?: number) => {
  if (value === undefined || !Number.isFinite(value)) return '—'
  return `${value.toFixed(2)}%`
}

const normalizeApprovalDetails = (data: {
  withdrawalApprovalDate?: string
  amountReceivableDate?: string
  grossMoneyToBeReceived?: number
  charges?: { exitLoad?: number; profitSharing?: number }
  netMoneyToBeReceived?: number
  withdrawalAmount?: string | number
  effectiveReturn?: number
}): WithdrawalApprovalDetails => ({
  withdrawalApprovalDate: data.withdrawalApprovalDate,
  amountReceivableDate: data.amountReceivableDate,
  grossMoneyToBeReceived: data.grossMoneyToBeReceived
    ? data.grossMoneyToBeReceived / 100
    : undefined,
  charges: {
    exitLoad: data.charges?.exitLoad ? data.charges.exitLoad / 100 : 0,
    profitSharing: data.charges?.profitSharing ? data.charges.profitSharing / 100 : 0,
  },
  netMoneyToBeReceived: data.netMoneyToBeReceived ? data.netMoneyToBeReceived / 100 : undefined,
  withdrawalAmount: data.withdrawalAmount ? Number(data.withdrawalAmount) / 100 : undefined,
  effectiveReturn: data.effectiveReturn,
})

const getStepPhases = (status: string) => {
  switch (status) {
    case WithdrawalStatus.TRADE_EXECUTED:
      return { request: 'done' as const, executed: 'done' as const }
    case WithdrawalStatus.ORDER_PLACED:
      return { request: 'done' as const, executed: 'current' as const }
    case WithdrawalStatus.INITIATED:
    default:
      return { request: 'current' as const, executed: 'upcoming' as const }
  }
}

const APPROVAL_DETAIL_STATUSES = [
  WithdrawalStatus.ORDER_PLACED,
  WithdrawalStatus.TRADE_EXECUTED,
] as const

export const OngoingWithdrawalDetailsModal: React.FC<OngoingWithdrawalDetailsModalProps> = ({
  isOpen,
  withdrawal,
  authToken,
  clientId,
  onClose,
}) => {
  const [approvalDetails, setApprovalDetails] = useState<WithdrawalApprovalDetails | null>(null)
  const [loadingApproval, setLoadingApproval] = useState(false)

  const resetAndClose = useCallback(() => {
    setApprovalDetails(null)
    setLoadingApproval(false)
    onClose()
  }, [onClose])

  useEffect(() => {
    if (!isOpen || !withdrawal) {
      setApprovalDetails(null)
      setLoadingApproval(false)
      return
    }

    if (withdrawal.approvalDetails) {
      setApprovalDetails(withdrawal.approvalDetails)
      setLoadingApproval(false)
      return
    }

    if (
      !APPROVAL_DETAIL_STATUSES.includes(
        withdrawal.status as (typeof APPROVAL_DETAIL_STATUSES)[number],
      )
    ) {
      setApprovalDetails(null)
      setLoadingApproval(false)
      return
    }

    let cancelled = false
    const load = async () => {
      setLoadingApproval(true)
      try {
        const response = await withdrawalServices.getWithdrawalApprovalDetails(
          String(withdrawal.id),
          { Authorization: authToken },
        )
        if (cancelled) return
        if (response?.status === 200 && response?.data) {
          setApprovalDetails(normalizeApprovalDetails(response.data))
        } else {
          setApprovalDetails(null)
        }
      } catch {
        if (!cancelled) setApprovalDetails(null)
      } finally {
        if (!cancelled) setLoadingApproval(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [isOpen, withdrawal, authToken])

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') resetAndClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isOpen, resetAndClose])

  const requestMetadata = withdrawal?.metadata as WithdrawalRequestMetadata | undefined
  const displayClientId = clientId || requestMetadata?.clientId

  const amountDisplay = useMemo(() => {
    if (!withdrawal) return '—'
    return withUpperScaleSuffix(displayDollar(String(withdrawal.amount), 'USD', 2, false))
  }, [withdrawal])

  const phases = useMemo(() => (withdrawal ? getStepPhases(withdrawal.status) : null), [withdrawal])

  const timelineItems = useMemo(() => {
    if (!withdrawal) return []
    const meta = withdrawal.metadata
    return [
      { label: 'Request initiated', value: formatDate(meta?.withdrawalInitiatedDate) },
      { label: 'Order placed', value: formatDate(meta?.withdrawalOrderPlacedDate) },
      { label: 'Trade executed', value: formatDate(meta?.tradeExecutionDate) },
      { label: 'Cancelled', value: formatDate(meta?.withdrawalCancelledDate) },
      { label: 'Rejected', value: formatDate(meta?.withdrawalRejectedDate) },
    ].filter((item) => item.value !== '—')
  }, [withdrawal])

  const withdrawalAmountDetail = useMemo(() => {
    if (!withdrawal) return '—'
    if (
      requestMetadata?.redeemMode === 'units' &&
      requestMetadata.withdrawalUnits !== undefined &&
      Number.isFinite(requestMetadata.withdrawalUnits)
    ) {
      const units = requestMetadata.withdrawalUnits.toLocaleString(undefined, {
        maximumFractionDigits: 4,
      })
      return `${units} units (${amountDisplay})`
    }
    if (
      requestMetadata?.withdrawalAmount !== undefined &&
      Number.isFinite(requestMetadata.withdrawalAmount)
    ) {
      return formatUsdFromDollars(requestMetadata.withdrawalAmount)
    }
    return amountDisplay
  }, [withdrawal, requestMetadata, amountDisplay])

  if (!isOpen || !withdrawal) return null

  return (
    <div
      className='dashboard-v2__withdrawal-modal-backdrop'
      role='presentation'
      onClick={(e) => {
        if (e.target === e.currentTarget) resetAndClose()
      }}
    >
      <div
        className='dashboard-v2__withdrawal-form'
        role='dialog'
        aria-modal='true'
        aria-labelledby='ongoing-withdrawal-details-title'
        onClick={(e) => e.stopPropagation()}
        data-testid='ongoing-withdrawal-details-modal'
      >
        <header className='dashboard-v2__withdrawal-form__header'>
          <div className='dashboard-v2__withdrawal-form__header-text'>
            <p className='dashboard-v2__withdrawal-form__eyebrow'>Ongoing redemption</p>
            <h2 id='ongoing-withdrawal-details-title'>Redemption details</h2>
            <div className='dashboard-v2__withdrawal-form__asset-chip'>
              <span className='dashboard-v2__withdrawal-form__asset-chip-title'>
                {withdrawal.opportunity_title || 'Redemption'}
              </span>
              {withdrawal.asset_class ? (
                <span className='dashboard-v2__withdrawal-form__asset-chip-badge'>
                  {withdrawal.asset_class}
                </span>
              ) : null}
            </div>
            <p className='dashboard-v2__withdrawal-form__subtitle'>
              Status: {displayStatus(withdrawal.status)} · Amount: {amountDisplay}
            </p>
          </div>
          <div
            role='button'
            tabIndex={0}
            aria-label='Close dialog'
            className='dashboard-v2__holdings-actions-panel__close'
            onClick={resetAndClose}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                resetAndClose()
              }
            }}
          >
            ×
          </div>
        </header>

        <div className='dashboard-v2__withdrawal-form__form'>
          {phases ? (
            <section className='dashboard-v2__withdrawal-form__section'>
              <h3 className='dashboard-v2__withdrawal-form__section-title'>Progress</h3>
              <DashboardV2ProgressStepper request={phases.request} executed={phases.executed} />
            </section>
          ) : null}

          <section className='dashboard-v2__withdrawal-form__section'>
            <h3 className='dashboard-v2__withdrawal-form__section-title'>Request information</h3>
            <div className='dashboard-v2__withdrawal-form__details-grid'>
              <DetailItem
                label='Underlying asset'
                value={requestMetadata?.underlyingAsset || withdrawal.opportunity_title || '—'}
              />
              <DetailItem label='Redemption amount' value={withdrawalAmountDetail} />
              {requestMetadata?.clientName ? (
                <DetailItem label='Client' value={requestMetadata.clientName} />
              ) : null}
              {displayClientId ? <DetailItem label='Client ID' value={displayClientId} /> : null}
              {withdrawal.platform_fee?.amount ? (
                <DetailItem
                  label='Platform fee'
                  value={formatUsdFromCents(withdrawal.platform_fee.amount)}
                />
              ) : null}
            </div>
          </section>

          {timelineItems.length > 0 ? (
            <section className='dashboard-v2__withdrawal-form__section'>
              <h3 className='dashboard-v2__withdrawal-form__section-title'>Timeline</h3>
              <div className='dashboard-v2__withdrawal-form__details-grid'>
                {timelineItems.map((item) => (
                  <DetailItem key={item.label} label={item.label} value={item.value} />
                ))}
              </div>
            </section>
          ) : null}

          {loadingApproval ? (
            <section className='dashboard-v2__withdrawal-form__section'>
              <h3 className='dashboard-v2__withdrawal-form__section-title'>Settlement details</h3>
              <div className='dashboard-v2__withdrawal-form__loading' aria-busy='true'>
                <span className='dashboard-v2__withdrawal-form__loading-spinner' aria-hidden />
                <p>Loading settlement details…</p>
              </div>
            </section>
          ) : approvalDetails ? (
            <section className='dashboard-v2__withdrawal-form__section'>
              <h3 className='dashboard-v2__withdrawal-form__section-title'>Settlement details</h3>
              <div className='dashboard-v2__withdrawal-form__details-grid'>
                <DetailItem
                  label='Redemption approval date'
                  value={formatDate(approvalDetails.withdrawalApprovalDate)}
                />
                <DetailItem
                  label='Amount receivable date'
                  value={formatDate(approvalDetails.amountReceivableDate)}
                />
                <DetailItem
                  label='Redemption amount'
                  value={formatUsdFromDollars(approvalDetails.withdrawalAmount)}
                />
                <DetailItem
                  label='Gross money to be received'
                  value={formatUsdFromDollars(approvalDetails.grossMoneyToBeReceived)}
                />
                <DetailItem
                  label='Profit sharing'
                  value={formatUsdFromDollars(approvalDetails.charges?.profitSharing)}
                />
                <DetailItem
                  label='Exit load'
                  value={formatUsdFromDollars(approvalDetails.charges?.exitLoad)}
                />
                <DetailItem
                  label='Net money to be received'
                  value={formatUsdFromDollars(approvalDetails.netMoneyToBeReceived)}
                />
                <DetailItem
                  label='Effective return'
                  value={formatPercent(approvalDetails.effectiveReturn)}
                />
              </div>
            </section>
          ) : null}
        </div>

        <div className='dashboard-v2__withdrawal-form__footer'>
          <p className='dashboard-v2__withdrawal-form__footer-note'>
            This redemption is in progress. Settlement details appear once approved.
          </p>
          <div className='dashboard-v2__withdrawal-form__footer-actions'>
            <button type='button' className='secondary' onClick={resetAndClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
